import { useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { useAuthStore } from "@/features/auth/stores/authStore";
import {
  usePaymentThresholdOverrideQueue,
  useRevokePaymentThresholdOverride,
} from "../hooks/useOrders";
import { ordersApi, type PaymentThresholdOverrideQueueItem } from "../services/ordersApi";
import { queryClient } from "@/shared/services/queryClient";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const FILTERS = [
  "Pending",
  "Approved",
  "Rejected",
  "Revoked",
  "Superseded",
  "Expired",
] as const;

type FilterValue = (typeof FILTERS)[number];

const formatDateTime = (iso?: string) => {
  if (!iso) return "-";
  try {
    return format(parseISO(iso), "dd MMM yyyy HH:mm");
  } catch {
    return "-";
  }
};

const formatKes = (value: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    minimumFractionDigits: 2,
  }).format(value);

const statusVariant = (status: string): "default" | "secondary" | "outline" | "destructive" => {
  if (status === "Approved") return "default";
  if (status === "Pending") return "secondary";
  if (status === "Rejected" || status === "Revoked") return "destructive";
  return "outline";
};

export const PaymentThresholdOverridesPage = () => {
  const { hasPermission } = useAuthStore();
  const canApprove = hasPermission("orders.approve_payment_threshold_override");
  const canRevoke = hasPermission("orders.revoke_payment_threshold_override");

  const [filter, setFilter] = useState<FilterValue>("Pending");
  const [reviewTarget, setReviewTarget] = useState<PaymentThresholdOverrideQueueItem | null>(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [revokeTarget, setRevokeTarget] = useState<PaymentThresholdOverrideQueueItem | null>(null);
  const [revokeReason, setRevokeReason] = useState("");

  const queueQuery = usePaymentThresholdOverrideQueue(filter);

  const rows = useMemo(() => {
    const raw = queueQuery.data ?? [];
    return raw.filter((row) => row.status !== "NoLongerRequired");
  }, [queueQuery.data]);

  const revokeMutation = useRevokePaymentThresholdOverride();

  const closeReview = () => {
    setReviewTarget(null);
    setReviewNotes("");
  };

  const closeRevoke = () => {
    setRevokeTarget(null);
    setRevokeReason("");
  };

  const refreshQueue = () => {
    queueQuery.refetch();
  };

  const decide = async (decision: "approve" | "reject") => {
    if (!reviewTarget) return;
    try {
      await ordersApi.decidePaymentThresholdOverride(reviewTarget.id, decision, reviewNotes.trim() || undefined);
      toast.success(`Override ${decision}d`);
      closeReview();
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["orders", reviewTarget.orderId, "payment-threshold-overrides"] });
      queryClient.invalidateQueries({ queryKey: ["orders", reviewTarget.orderId, "work-authorization-status"] });
      queryClient.invalidateQueries({ queryKey: ["orders", "payment-threshold-overrides", "queue"] });
      refreshQueue();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not complete review");
    }
  };

  const revoke = async () => {
    if (!revokeTarget) return;
    const reason = revokeReason.trim();
    if (!reason) {
      toast.error("Revocation reason is required");
      return;
    }
    try {
      await revokeMutation.mutateAsync({ id: revokeTarget.id, reason });
      toast.success("Override revoked");
      closeRevoke();
      refreshQueue();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not revoke override");
    }
  };

  if (queueQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading payment threshold override queue...</div>;
  }

  if (queueQuery.error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load payment threshold override queue.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Payment Threshold Overrides</h1>
          <p className="text-muted-foreground mt-1">
            Centralized maker-checker queue for work authorization overrides when payment threshold is not met.
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Requesters submit from Orders. Approvers review here. Outcome updates Order work authorization status.
          </p>
        </div>
        <Button variant="outline" onClick={refreshQueue}>Refresh</Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((value) => (
          <Button
            key={value}
            size="sm"
            variant={filter === value ? "default" : "outline"}
            onClick={() => setFilter(value)}
          >
            {value}
          </Button>
        ))}
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border bg-muted/40 p-8 text-center text-muted-foreground">
          No {filter.toLowerCase()} payment-threshold overrides.
        </div>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>Order #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead>Decision Time</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const decisionTime = row.approvedAt ?? row.rejectedAt ?? row.revokedAt;
                return (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Badge variant={statusVariant(row.status)}>{row.status}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{row.orderNumber}</TableCell>
                    <TableCell>{row.customerName || "-"}</TableCell>
                    <TableCell className="text-sm">
                      <div className="space-y-0.5">
                        <p>{row.actualPaidPercentage.toFixed(1)}% paid</p>
                        <p className="text-xs text-muted-foreground">Required {row.requiredThresholdPercentage}%</p>
                        <p className="text-xs text-muted-foreground">
                          {formatKes(row.paidAmount)} / {formatKes(row.requiredAmount)}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[320px] truncate" title={row.reason}>{row.reason}</TableCell>
                    <TableCell>{formatDateTime(row.requestedAt)}</TableCell>
                    <TableCell>{formatDateTime(decisionTime)}</TableCell>
                    <TableCell className="text-right space-x-2">
                      {row.status === "Pending" && canApprove && (
                        <Button size="sm" onClick={() => setReviewTarget(row)}>
                          Review Override Request
                        </Button>
                      )}
                      {row.status === "Approved" && canRevoke && (
                        <Button size="sm" variant="destructive" onClick={() => setRevokeTarget(row)}>
                          Revoke Override
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!reviewTarget} onOpenChange={(open) => !open && closeReview()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Review Override Request</DialogTitle>
            <DialogDescription>
              {reviewTarget ? `Order ${reviewTarget.orderNumber}` : "Pending override request"}
            </DialogDescription>
          </DialogHeader>
          {reviewTarget && (
            <div className="space-y-4">
              <div className="rounded-md border p-3 text-sm space-y-1">
                <p><strong>Customer:</strong> {reviewTarget.customerName || "-"}</p>
                <p><strong>Reason:</strong> {reviewTarget.reason}</p>
                <p><strong>Required:</strong> {reviewTarget.requiredThresholdPercentage}% ({formatKes(reviewTarget.requiredAmount)})</p>
                <p><strong>Actually paid:</strong> {reviewTarget.actualPaidPercentage.toFixed(1)}% ({formatKes(reviewTarget.paidAmount)})</p>
                <p><strong>Submitted:</strong> {formatDateTime(reviewTarget.requestedAt)}</p>
              </div>
              <Textarea
                placeholder="Approval or rejection notes"
                value={reviewNotes}
                onChange={(event) => setReviewNotes(event.target.value)}
              />
              <DialogFooter>
                  <Button variant="destructive" onClick={() => decide("reject")}>
                  Reject
                </Button>
                  <Button onClick={() => decide("approve")}>
                  Approve
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!revokeTarget} onOpenChange={(open) => !open && closeRevoke()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Revoke Override</DialogTitle>
            <DialogDescription>
              {revokeTarget ? `Order ${revokeTarget.orderNumber}` : "Approved override"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Why is this approved override being revoked?"
              value={revokeReason}
              onChange={(event) => setRevokeReason(event.target.value)}
            />
            <DialogFooter>
              <Button variant="secondary" onClick={closeRevoke}>Cancel</Button>
              <Button variant="destructive" onClick={revoke} disabled={revokeMutation.isPending || !revokeReason.trim()}>
                {revokeMutation.isPending ? "Revoking..." : "Revoke Override"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentThresholdOverridesPage;
